"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheTorchlighterVision() {
  const {
    ref: heroRef,
    isVisible: heroVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: quoteRef,
    isVisible: quoteVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <section data-section-id="4489" id="the-torchlighter-vision" className="relative bg-background text-foreground pt-28 md:pt-[140px] pb-20 md:pb-32 overflow-hidden">
      {}
      <div ref={heroRef} className={`w-full px-4 md:px-8 lg:px-12 transition-all duration-1000 ease-out ${heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="relative w-full aspect-[21/9] overflow-hidden rounded-lg shadow-lg">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/community_gathering_park_event.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/community_gathering_park_event.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/community_gathering_park_event.png-wvc-sizes" alt="Families gathering at a community park event" loading="lazy" decoding="async" className="w-full h-full object-cover" style={{
          filter: "sepia(0.05) contrast(1.05)"
        }} />
        </div>
        <p className="mt-4 text-xs tracking-[0.3em] uppercase font-default" style={{
        color: "var(--secondary)"
      }}>
          A Day of Connection — Hope Torchlighters Community, 2024
        </p>
      </div>

      {}
      <div ref={contentRef} className="mt-16 md:mt-24 lg:mt-[100px] px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 max-w-[1400px] mx-auto">
          {}
          <div className={`lg:col-span-5 lg:col-start-1 lg:mt-10 transition-all duration-1000 delay-200 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-xs tracking-[0.35em] uppercase font-default mb-6" style={{
            color: "var(--secondary)"
          }}>
              Our Vision
            </p>

            <h2 className="font-default font-light text-foreground leading-[1.05] text-4xl sm:text-5xl md:text-6xl lg:text-[72px] mb-10">
              A community built <em className="italic" style={{
              color: "var(--secondary)"
            }}>around</em> families.
            </h2>

            <div className="font-default text-foreground/85 text-[17px] md:text-[18px] leading-[1.75] max-w-[50ch] space-y-6">
              <p className="first-letter:font-default first-letter:text-[88px] md:first-letter:text-[96px] first-letter:leading-[0.85] first-letter:float-left first-letter:mr-3 first-letter:mt-2 first-letter:font-medium" style={{
              ["--tw-prose-first-letter" as never]: "var(--secondary)"
            }}>
                <span style={{
                color: "inherit"
              }}>
                  Hope Torchlighters began not as a program, but as a <em className="italic">shared promise</em> — that neurodiverse families deserve a village that truly understands. We founded this organization on a single principle: support should feel, above all, like belonging — only stronger.
                </span>
              </p>

              <p>
                For years, our founders worked alongside families navigating the complexities of special needs care. We saw a need for <em className="italic">compassion and advocacy</em> — respite care in place of isolation, bilingual resources in place of barriers, and a philosophy that places the unique joy of every child at the center of our mission.
              </p>

              <p>
                Today, Hope Torchlighters remains a community-driven nonprofit. Our reach is intentionally personal. Our commitment is intentionally unwavering. Every initiative — from our inclusive family events to our dedicated caregiver support — is designed with one clear goal: that every family who connects with us feels more empowered than when they arrived.
              </p>
            </div>
          </div>

          {}
          <div className={`lg:col-span-5 lg:col-start-8 lg:-mr-4 xl:-mr-8 transition-all duration-1000 delay-400 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="relative w-full aspect-[4/5] overflow-hidden rounded-lg shadow-xl">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/founder_team_support_portrait.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/founder_team_support_portrait.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/founder_team_support_portrait.png-wvc-sizes" alt="Hope Torchlighters leadership team supporting families" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
            <p className="mt-4 text-xs tracking-[0.3em] uppercase font-default" style={{
            color: "var(--secondary)"
          }}>
              Our Leadership Team, 2023
            </p>

            {}
            <div ref={quoteRef} className={`mt-12 md:mt-16 relative pl-8 md:pl-10 transition-all duration-1000 ease-out ${quoteVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <span aria-hidden="true" className="absolute left-0 top-0 font-default text-6xl md:text-7xl leading-none select-none" style={{
              color: "var(--secondary)"
            }}>
                &ldquo;
              </span>
              <blockquote className="font-default italic text-2xl md:text-[32px] leading-[1.35] pr-6" style={{
              color: "var(--primary)"
            }}>
                Every family deserves a village that feels like home — only better.
              </blockquote>
              <span aria-hidden="true" className="absolute right-0 bottom-[-1rem] font-default text-6xl md:text-7xl leading-none select-none" style={{
              color: "var(--secondary)"
            }}>
                &rdquo;
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}